import React from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { SpotlightCard } from './SpotlightCard';
import { KeyboardIcon } from './icons/KeyboardIcon';
import { TargetIcon } from './icons/TargetIcon';
import { MusicalNoteIcon } from './icons/MusicalNoteIcon';
import { GameControllerIcon } from './icons/GameControllerIcon';
import { LinkIcon } from './icons/LinkIcon';
import { ChatBubbleIcon } from './icons/ChatBubbleIcon';
import { useTranslation } from '../i18n/LanguageContext';

interface DifferentiatorItem {
  key?: string;
  title: string;
  description: string;
  isLarge?: boolean;
  hasVideoBg?: boolean;
}

const icons: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  practical: KeyboardIcon,
  goals: TargetIcon,
  customMaterial: MusicalNoteIcon,
  gamification: GameControllerIcon,
  platform: LinkIcon,
  support: ChatBubbleIcon,
};

const DifferentiatorCardContent: React.FC<{
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  title?: string;
  description?: string;
  isLarge?: boolean;
}> = ({ icon: Icon, title = '', description = '', isLarge = false }) => (
  <>
    <div className={`text-brand-accent ${isLarge ? 'w-12 h-12' : 'w-10 h-10'} mb-4 flex items-center justify-center`} aria-hidden="true">
      {Icon ? <Icon className="w-full h-full" /> : <MusicalNoteIcon className="w-full h-full" />}
    </div>
    <h3 className={`${isLarge ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'} font-serif font-semibold text-brand-light mb-3`}>
      {title}
    </h3>
    <p className={`${isLarge ? 'text-base' : 'text-sm'} text-brand-text flex-grow`}>
      {description}
    </p>
  </>
);

export const MethodologyDifferentiatorsSection: React.FC = () => {
  const { t } = useTranslation();
  const rawDifferentiators = t('methodology.differentiators');
  const differentiators: DifferentiatorItem[] = Array.isArray(rawDifferentiators) ? rawDifferentiators : [];

  const largeItem = differentiators.find(d => d?.isLarge);
  const smallItems = differentiators.filter(d => !d?.isLarge);
  
  const cardBaseClasses = `bg-brand-primary/60 backdrop-filter backdrop-blur-md border border-slate-600/40 rounded-xl p-6 shadow-2xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col text-left h-full`;

  return (
    <section id="metodologia" className="py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation animation="slideInUp">
          <SectionTitle
            title={t('methodology.title')}
            subtitle={t('methodology.subtitle')}
          />
        </ScrollAnimation>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {largeItem && (
            <ScrollAnimation animation="scaleIn" delay={150} className="md:col-span-2 md:row-span-2">
              <SpotlightCard className={cardBaseClasses}>
                <DifferentiatorCardContent
                  icon={largeItem.key ? icons[largeItem.key] : undefined}
                  title={largeItem.title}
                  description={largeItem.description}
                  isLarge={largeItem.isLarge}
                />
              </SpotlightCard>
            </ScrollAnimation>
          )}

          {smallItems.map((item, index) => {
            const Icon = item.key ? icons[item.key] : undefined;
            return (
              <ScrollAnimation key={item.key || `diff-${index}`} animation="scaleIn" delay={200 + index * 50}>
                <SpotlightCard className={cardBaseClasses}>
                  {item.hasVideoBg && (
                    <>
                      <video
                        src="https://nikolasyuri.com.br/wp/wp-content/uploads/2026/08/Falling_Notes.mp4"
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover -z-10 rounded-xl"
                      />
                      <div className="absolute inset-0 bg-brand-primary/60 -z-10 rounded-xl" />
                    </>
                  )}
                  <DifferentiatorCardContent
                    icon={Icon}
                    title={item.title}
                    description={item.description}
                    isLarge={item.isLarge}
                  />
                </SpotlightCard>
              </ScrollAnimation>
            );
          })}
        </div>
      </div>
    </section>
  );
};
